/**
 * Query Invalidation Utilities
 *
 * Centralized functions for invalidating related query caches.
 * Ensures consistency across all components that manipulate data.
 */

import type { QueryClient } from '@tanstack/react-query';

import { queryKeys } from './queryKeys';

/**
 * Invalidate all version-related queries
 *
 * Call this after any version operation (create, update, delete, tag changes).
 * Ensures all UI components showing version data are refreshed.
 *
 * @param queryClient - TanStack Query client
 * @param tableId - Table ID (required for table-specific invalidation)
 *
 * @example
 * ```tsx
 * const queryClient = useQueryClient();
 * await invalidateVersionQueries(queryClient, tableId);
 * ```
 */
export async function invalidateVersionQueries(
  queryClient: QueryClient,
  tableId: string | undefined
): Promise<void> {
  await Promise.all([
    // Version pickers (useTableVersionsQuery, VersionCombobox)
    tableId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.tables.versions(tableId),
        })
      : Promise.resolve(),

    // Table detail (storage_bytes changes when versions added/deleted)
    tableId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.tables.detail(tableId),
        })
      : Promise.resolve(),

    // Tables list (storage_bytes shown in explorer)
    queryClient.invalidateQueries({
      queryKey: queryKeys.tables.all,
    }),

    // VersionsTable data grid
    queryClient.invalidateQueries({
      queryKey: queryKeys.dataGrid.versions,
    }),
  ]);
}
